import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useFilterMachineFixings } from "../../hooks/useFilterMachineFixings";
import { useFilterTiltrotators } from "../../hooks/useFilterTiltrotators";
import { useScrollToProduct } from "../../hooks/useScrollToProduct";
import { GrappleModule } from "../../interfaces/api/GrappleModule";
import { LowerBracket } from "../../interfaces/api/LowerBracket";
import { MachineFixing } from "../../interfaces/api/MachineFixing";
import { Rototilt } from "../../interfaces/api/Rototilt";
import { UpperBracket } from "../../interfaces/api/UpperBracket";
import { MachineFixingType } from "../../shared/enums/MachineFixingType";
import { ProductCategory } from "../../shared/enums/ProductCategory";
import { ProductType } from "../../shared/enums/ProductType";
import { ListObject } from "../../shared/types/ListObject";
import { ProductItem } from "../../shared/types/ProductItem";
import { action } from "../../store/action";
import { useProductByType } from "../../store/hooks/useProductByType";
import { ProductData } from "../../store/interfaces/productData";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import MachineFixingsProduct from "../MachineFixings/MachineFixingsProduct";
import TiltrotatorsProduct from "../Tiltrotator/TiltrotatorsProduct";
import ToolsProduct from "../Tools/ToolsProduct";
import { useFilterGrappleModules } from "./hooks/useFilterGrappleModules";
import { useFilterLowerBrackets } from "./hooks/useFilterLowerBrackets";
import { useFilterUpperBrackets } from "./hooks/useFilterUpperBrackets";
import { useHasSelectedOptionalTools } from "./hooks/useHasSelectedOptionalTools";
import { useStateCleaner } from "./hooks/useStateCleaner";
import "./Product.scss";
import ProductCategories from "./ProductCategories";

interface ProductProps {
  isNoneGrappleModuleSelected: boolean;
  selectedGrappleModule: GrappleModule;
  selectedMachineFixing: MachineFixing;
  selectedMachineFixingType: MachineFixingType;
  selectedLowerBracket: LowerBracket;
  selectedProductCategory: ProductCategory;
  selectedTiltrotator: Rototilt;
  selectedToolsProductItems: ProductItem[];
  selectedToolsProductTypes: ProductType[];
  selectedUpperBracket: UpperBracket;
}

enum ConfirmMode {
  None = -1,
  ProductCategory = 0,
  MachineFixingType = 1,
  Tiltrotator = 2,
  LowerBracket = 3,
  MachineFixing = 4,
}

const Product = ({
  isNoneGrappleModuleSelected,
  selectedGrappleModule,
  selectedMachineFixing,
  selectedMachineFixingType,
  selectedLowerBracket,
  selectedProductCategory,
  selectedTiltrotator,
  selectedToolsProductItems = [],
  selectedToolsProductTypes = [],
  selectedUpperBracket,
}: ProductProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("popup");
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(ConfirmMode.None);
  const [oldProductCategory, setOldProductCategory] =
    useState<ProductCategory>(undefined);
  const [oldMachineFixingType, setOldMachineFixingType] =
    useState<MachineFixingType>();
  const [oldTiltrotator, setOldTiltrotator] = useState<Rototilt>(undefined);
  const [oldMachineFixing, setOldMachineFixing] =
    useState<MachineFixing>(undefined);
  const [oldLowerBracket, setOldLowerBracket] =
    useState<LowerBracket>(undefined);
  const hasSelectedOptionalTools = useHasSelectedOptionalTools();
  const {
    refGrappleModules,
    refLowerBrackets,
    refMachineFixingList,
    refMachineFixings,
    refMachineFixingsProduct,
    refTiltrotators,
    refToolsItems,
    refToolsProduct,
    refToolsProductItemsList,
    refUpperBrackets,
    scrollToGrappleModules,
    scrollToLowerBrackets,
    scrollToMachineFixings,
    scrollToMachineFixingsProduct,
    scrollToTiltrotators,
    scrollToToolsProduct,
    scrollToToolsProductItemsList,
    scrollToUpperBrackets,
  } = useScrollToProduct();
  const tiltrotatorsData = useProductByType(
    ProductType.Rototilts
  ) as ProductData;
  const machineFixingsData = useProductByType(
    ProductType.MachineFixings
  ) as ProductData;
  const upperBracketsData = useProductByType(
    ProductType.UpperBrackets
  ) as ProductData;
  const lowerBracketsData = useProductByType(
    ProductType.LowerBrackets
  ) as ProductData;
  const grappleModulesData = useProductByType(
    ProductType.GrappleModules
  ) as ProductData;
  const {
    filteredTiltrotators,
    tiltrotatorsMaxWeight: tiltrotatorsMaxWight,
    tiltrotatorsMinWeight: tiltrotatorsMinWight,
  } = useFilterTiltrotators({
    tiltrotatorsData,
  });
  const { filteredMachineFixings } = useFilterMachineFixings({
    machineFixingsData,
    selectedProductCategory,
    selectedTiltrotator,
  });
  const { filteredUpperBrackets } = useFilterUpperBrackets({
    upperBracketsData,
    selectedTiltrotator,
  });
  const { filteredLowerBrackets } = useFilterLowerBrackets({
    lowerBracketsData,
    selectedUpperBracket,
    selectedTiltrotator,
  });
  const { filteredGrappleModules } = useFilterGrappleModules({
    grappleModulesData,
    lowerBracket: selectedLowerBracket,
    tiltrotator: selectedTiltrotator,
  });

  const {
    clearStateForProductCategory,
    clearStateForOptionalTools,
    clearStateForProductCategoryTiltrotator,
    clearStateForProductMachineFixingType,
  } = useStateCleaner();

  const onProductCategoryChange = (productCategory: ProductCategory) => {
    setOldProductCategory(selectedProductCategory);
    dispatch(action.saveProductCategory(productCategory));

    if (
      selectedProductCategory !== undefined &&
      (selectedTiltrotator ||
        selectedMachineFixingType ||
        selectedToolsProductTypes.length > 0)
    ) {
      setConfirmMode(ConfirmMode.ProductCategory);
      setShowConfirmDialog(true);
      return;
    }

    productCategoryChanged(productCategory);
  };

  const productCategoryChanged = (productCategory: ProductCategory) => {
    clearStateForProductCategory();

    if (productCategory === ProductCategory.Tiltrotator) {
      scrollToTiltrotators();
    }
    if (productCategory === ProductCategory.MachineFixings) {
      scrollToMachineFixingsProduct();
    }
    if (productCategory === ProductCategory.Tools) {
      scrollToToolsProduct();
    }
  };

  const onMachineFixingTypeChange = (machineFixingType: MachineFixingType) => {
    setOldMachineFixingType(selectedMachineFixingType);
    dispatch(action.saveProductMachineFixingType(machineFixingType));

    if (
      selectedMachineFixingType !== undefined &&
      (selectedMachineFixing || selectedUpperBracket)
    ) {
      setConfirmMode(ConfirmMode.MachineFixingType);
      setShowConfirmDialog(true);
      return;
    }

    machineFixingTypeChanged(machineFixingType);
  };

  const machineFixingTypeChanged = (machineFixingType: MachineFixingType) => {
    clearStateForProductMachineFixingType();

    if (
      hasSelectedOptionalTools &&
      selectedProductCategory === ProductCategory.Tiltrotator
    ) {
      clearStateForOptionalTools();
    }

    if (machineFixingType === MachineFixingType.NoMachineFixing) {
      scrollToUpperBrackets();
    } else {
      scrollToMachineFixings();
    }
  };

  const onTiltrotatorChange = (tiltrotator: Rototilt) => {
    setOldTiltrotator(selectedTiltrotator);
    dispatch(action.saveCartSelectionsProductTiltrotator(tiltrotator));

    if (selectedTiltrotator && selectedMachineFixingType) {
      setConfirmMode(ConfirmMode.Tiltrotator);
      setShowConfirmDialog(true);
      return;
    }

    tiltrotatorChanged();
  };

  const tiltrotatorChanged = () => {
    clearStateForProductCategoryTiltrotator();
    scrollToMachineFixingsProduct();
  };

  const onMachineFixingChange = (machineFixing: MachineFixing) => {
    setOldMachineFixing(selectedMachineFixing);
    dispatch(action.saveCartSelectionsProductMachineFixing(machineFixing));

    if (
      hasSelectedOptionalTools &&
      selectedProductCategory === ProductCategory.Tiltrotator &&
      selectedTiltrotator &&
      selectedMachineFixing
    ) {
      setConfirmMode(ConfirmMode.MachineFixing);
      setShowConfirmDialog(true);
      return;
    }

    machineFixingChanged();
  };

  const machineFixingChanged = () => {
    if (
      hasSelectedOptionalTools &&
      selectedProductCategory === ProductCategory.Tiltrotator
    ) {
      clearStateForOptionalTools();
    }

    if (
      selectedProductCategory === ProductCategory.Tiltrotator &&
      !isNoneGrappleModuleSelected
    ) {
      scrollToLowerBrackets();
    }
  };

  const onUpperBracketChange = (upperBracket: UpperBracket) => {
    dispatch(action.saveCartSelectionsProductUpperBracket(upperBracket));
    scrollToLowerBrackets();
  };

  const onLowerBracketChange = (lowerBracket: LowerBracket) => {
    setOldLowerBracket(selectedLowerBracket);
    dispatch(action.saveCartSelectionsProductLowerBracket(lowerBracket));

    if (
      hasSelectedOptionalTools &&
      selectedProductCategory === ProductCategory.Tiltrotator &&
      selectedTiltrotator &&
      selectedLowerBracket
    ) {
      setConfirmMode(ConfirmMode.LowerBracket);
      setShowConfirmDialog(true);
      return;
    }

    lowerBracketChanged();
  };

  const lowerBracketChanged = () => {
    if (
      hasSelectedOptionalTools &&
      selectedProductCategory === ProductCategory.Tiltrotator
    ) {
      clearStateForOptionalTools();
    }

    dispatch(action.saveCartSelectionsProductNoGrappleModuleSelected(false));
    dispatch(action.saveCartSelectionsProductGrappleModule(undefined));

    scrollToGrappleModules();
  };

  const onGrappleModuleChange = (grappleModule: GrappleModule) => {
    dispatch(action.saveCartSelectionsProductGrappleModule(grappleModule));
    dispatch(action.saveCartSelectionsProductNoGrappleModuleSelected(false));
  };

  const onNoneGrapppleModuleChange = () => {
    dispatch(action.saveCartSelectionsProductNoGrappleModuleSelected(true));
    dispatch(action.saveCartSelectionsProductGrappleModule(undefined));
  };

  const onToolsProductTypeChange = (productType: ProductType) => {
    const index = selectedToolsProductTypes.indexOf(productType);

    if (index > -1) {
      const filteredSelectedToolsProductTypes =
        selectedToolsProductTypes.filter(
          (selectedProductType) => selectedProductType !== productType
        );

      dispatch(
        action.saveProductToolsProductTypes([
          ...filteredSelectedToolsProductTypes,
        ])
      );

      return;
    }

    dispatch(
      action.saveProductToolsProductTypes([
        ...selectedToolsProductTypes,
        productType,
      ])
    );

    scrollToToolsProductItemsList();
  };

  const onToolsListObjectChange = (productItem: ListObject) => {
    const index = selectedToolsProductItems.findIndex(
      (selectedToolsProductItem) =>
        selectedToolsProductItem.id === productItem.id
    );

    if (index > -1) {
      const filteredSelectedToolsProductItems =
        selectedToolsProductItems.filter(
          (selectedToolsProductItem) =>
            selectedToolsProductItem.id !== productItem.id
        );

      dispatch(
        action.saveCartSelectionsProductToolsProductItems([
          ...filteredSelectedToolsProductItems,
        ])
      );

      return;
    }

    dispatch(
      action.saveCartSelectionsProductToolsProductItems([
        ...selectedToolsProductItems,
        productItem as ProductItem,
      ])
    );
  };

  const onDialogOk = () => {
    switch (confirmMode) {
      case ConfirmMode.ProductCategory:
        productCategoryChanged(selectedProductCategory);
        break;
      case ConfirmMode.MachineFixingType:
        machineFixingTypeChanged(selectedMachineFixingType);
        break;
      case ConfirmMode.Tiltrotator:
        tiltrotatorChanged();
        break;
      case ConfirmMode.LowerBracket:
        lowerBracketChanged();
        break;
      case ConfirmMode.MachineFixing:
        machineFixingChanged();
        break;
    }

    setShowConfirmDialog(false);
  };

  const onDialogClose = () => {
    switch (confirmMode) {
      case ConfirmMode.ProductCategory:
        dispatch(action.saveProductCategory(oldProductCategory));
        break;
      case ConfirmMode.MachineFixingType:
        dispatch(action.saveProductMachineFixingType(oldMachineFixingType));
        break;
      case ConfirmMode.Tiltrotator:
        dispatch(action.saveCartSelectionsProductTiltrotator(oldTiltrotator));
        break;
      case ConfirmMode.LowerBracket:
        dispatch(action.saveCartSelectionsProductLowerBracket(oldLowerBracket));
        break;
      case ConfirmMode.MachineFixing:
        dispatch(
          action.saveCartSelectionsProductMachineFixing(oldMachineFixing)
        );
        break;
    }

    setShowConfirmDialog(false);
  };

  return (
    <div className="productContainer">
      <ProductCategories
        machineFixingsData={machineFixingsData}
        onProductCategoryChange={onProductCategoryChange}
        selectedProductCategory={selectedProductCategory}
        tiltrotatorInfo={{
          count: filteredTiltrotators ? filteredTiltrotators.length : 0,
          maxWeight: tiltrotatorsMaxWight,
          minWeight: tiltrotatorsMinWight,
        }}
      />
      {(selectedProductCategory === ProductCategory.Tiltrotator ||
        selectedProductCategory === ProductCategory.MachineFixings) && (
        <>
          {selectedProductCategory === ProductCategory.Tiltrotator && (
            <TiltrotatorsProduct
              onTiltrotatorChange={onTiltrotatorChange}
              refTiltrotators={refTiltrotators}
              selectedTiltrotator={selectedTiltrotator}
              tiltrotators={filteredTiltrotators}
            />
          )}
          {(selectedTiltrotator ||
            selectedProductCategory === ProductCategory.MachineFixings) && (
            <MachineFixingsProduct
              grappleModules={filteredGrappleModules}
              isNoneGrappleModuleSelected={isNoneGrappleModuleSelected}
              lowerBrackets={filteredLowerBrackets}
              machineFixnings={filteredMachineFixings}
              onGrappleModuleChange={onGrappleModuleChange}
              onLowerBracketChange={onLowerBracketChange}
              onMachineFixingChange={onMachineFixingChange}
              onMachineFixingTypeChange={onMachineFixingTypeChange}
              onNoneGrapppleModuleChange={onNoneGrapppleModuleChange}
              onUpperBracketChange={onUpperBracketChange}
              productCategory={selectedProductCategory}
              refGrappleModules={refGrappleModules}
              refLowerBrackets={refLowerBrackets}
              refMachineFixingList={refMachineFixingList}
              refMachineFixings={refMachineFixings}
              refMachineFixingsProduct={refMachineFixingsProduct}
              refUpperBrackets={refUpperBrackets}
              selectedGrappleModule={selectedGrappleModule}
              selectedLowerBracket={selectedLowerBracket}
              selectedMachineFixing={selectedMachineFixing}
              selectedMachineFixingType={selectedMachineFixingType}
              upperBrackets={filteredUpperBrackets}
              selectedUpperBracket={selectedUpperBracket}
              selectedTiltrotator={selectedTiltrotator}
            />
          )}
        </>
      )}
      {selectedProductCategory === ProductCategory.Tools && (
        <ToolsProduct
          onListObjectChange={onToolsListObjectChange}
          onProductTypeChange={onToolsProductTypeChange}
          refToolsItems={refToolsItems}
          refToolsProduct={refToolsProduct}
          refToolsProductItemsList={refToolsProductItemsList}
          selectedToolsProductItems={selectedToolsProductItems}
          selectedToolsProductTypes={selectedToolsProductTypes}
        />
      )}
      <ConfirmDialog
        dialogHeader={t("important")}
        text={t("areyousure")}
        okButtonText={t("clearchoises")}
        cancelButtonText={t("cancel")}
        open={showConfirmDialog}
        onClose={onDialogClose}
        onCancelClick={onDialogClose}
        onOkClick={onDialogOk}
      ></ConfirmDialog>
    </div>
  );
};

export default Product;
